export interface AssessmentSchemes {
  abas_3_førskole?: boolean;
  abas_3_førskole_foraeldre?: boolean;
  abas_3_førskole_paedagog?: boolean;
  abas_3_førskole_foraeldre_paedagog?: boolean;
  abas_3_skole?: boolean;
  abas_3_skole_foraeldre?: boolean;
  abas_3_skole_laerer?: boolean;
  abas_3_skole_foraeldre_laerer?: boolean;
  abas_3_voksen?: boolean;
  abas_3_voksen_selv?: boolean;
  abas_3_voksen_paaroerende?: boolean;
  abas_3_voksen_selv_paaroerende?: boolean;
  brief_2_førskole?: boolean;
  brief_2_førskole_foraeldre?: boolean;
  brief_2_førskole_paedagog?: boolean;
  brief_2_førskole_foraeldre_paedagog?: boolean;
  brief_2_skole?: boolean;
  brief_2_skole_foraeldre?: boolean;
  brief_2_skole_laerer?: boolean;
  brief_2_skole_foraeldre_laerer?: boolean;
  brief_2_voksen?: boolean;
  brief_2_voksen_selv?: boolean;
  brief_2_voksen_paaroerende?: boolean;
  brief_2_voksen_selv_paaroerende?: boolean;
  brief_selv?: boolean;
  brief_srs_multi_besvarelse?: boolean;
  srs_førskole?: boolean;
  srs_førskole_foraeldre?: boolean;
  srs_førskole_laerer?: boolean;
  srs_førskole_foraeldre_laerer?: boolean;
  srs_skole?: boolean;
  srs_skole_foraeldre?: boolean;
  srs_skole_laerer?: boolean;
  srs_skole_foraeldre_laerer?: boolean;
  vineland_førskole?: boolean;
  vineland_førskole_foraeldre?: boolean;
  vineland_førskole_laerer?: boolean;
  vineland_førskole_foraeldre_laerer?: boolean;
  vineland_skole?: boolean;
  vineland_skole_foraeldre?: boolean;
  vineland_skole_laerer?: boolean;
  vineland_skole_foraeldre_laerer?: boolean;
  andet?: boolean;
  andet_items?: string[];
}

export type AssessmentFlagKey = Exclude<keyof AssessmentSchemes, 'andet_items'>;

export type AssessmentRespondentGroup = {
  parent: AssessmentFlagKey;
  legacy: AssessmentFlagKey;
  title: string;
  respondents: ReadonlyArray<{ field: AssessmentFlagKey; label: string }>;
};

export type AssessmentSchemeItem =
  | { kind: 'group'; group: AssessmentRespondentGroup }
  | { kind: 'flag'; field: AssessmentFlagKey; label: string }
  | { kind: 'andet'; field: 'andet'; label: string };

function schemeGroup(group: AssessmentRespondentGroup): AssessmentSchemeItem {
  return { kind: 'group', group };
}

function schemeFlag(field: AssessmentFlagKey, label: string): AssessmentSchemeItem {
  return { kind: 'flag', field, label };
}

/**
 * Paper-form order for NPU "Elektroniske skemaer" (Rekvirering af sagsakter).
 * Render this array everywhere the list appears — do not sort or append extras after it.
 */
export const ASSESSMENT_SCHEME_ITEMS: readonly AssessmentSchemeItem[] = [
  schemeGroup({
    parent: 'abas_3_førskole',
    legacy: 'abas_3_førskole_foraeldre_paedagog',
    title: 'ABAS-3 førskole',
    respondents: [
      { field: 'abas_3_førskole_foraeldre', label: 'Forældre' },
      { field: 'abas_3_førskole_paedagog', label: 'Pædagog' },
    ],
  }),
  schemeGroup({
    parent: 'abas_3_skole',
    legacy: 'abas_3_skole_foraeldre_laerer',
    title: 'ABAS-3 skole',
    respondents: [
      { field: 'abas_3_skole_foraeldre', label: 'Forældre' },
      { field: 'abas_3_skole_laerer', label: 'Lærer' },
    ],
  }),
  schemeGroup({
    parent: 'abas_3_voksen',
    legacy: 'abas_3_voksen_selv_paaroerende',
    title: 'ABAS-3 voksen',
    respondents: [
      { field: 'abas_3_voksen_selv', label: 'Selv' },
      { field: 'abas_3_voksen_paaroerende', label: 'Andre' },
    ],
  }),
  schemeGroup({
    parent: 'brief_2_førskole',
    legacy: 'brief_2_førskole_foraeldre_paedagog',
    title: 'BRIEF-2 førskole',
    respondents: [
      { field: 'brief_2_førskole_foraeldre', label: 'Forældre' },
      { field: 'brief_2_førskole_paedagog', label: 'Pædagog' },
    ],
  }),
  schemeGroup({
    parent: 'brief_2_skole',
    legacy: 'brief_2_skole_foraeldre_laerer',
    title: 'BRIEF-2 skole',
    respondents: [
      { field: 'brief_2_skole_foraeldre', label: 'Forældre' },
      { field: 'brief_2_skole_laerer', label: 'Lærer' },
    ],
  }),
  schemeFlag('brief_selv', 'BRIEF 2 selv'),
  schemeGroup({
    parent: 'brief_2_voksen',
    legacy: 'brief_2_voksen_selv_paaroerende',
    title: 'BRIEF-2 voksen',
    respondents: [
      { field: 'brief_2_voksen_selv', label: 'Selv' },
      { field: 'brief_2_voksen_paaroerende', label: 'Pårørende' },
    ],
  }),
  schemeFlag('brief_srs_multi_besvarelse', 'BRIEF + SRS multi (besvarelse)'),
  schemeGroup({
    parent: 'srs_førskole',
    legacy: 'srs_førskole_foraeldre_laerer',
    title: 'SRS førskole',
    respondents: [
      { field: 'srs_førskole_foraeldre', label: 'Forældre' },
      { field: 'srs_førskole_laerer', label: 'Lærer' },
    ],
  }),
  schemeGroup({
    parent: 'srs_skole',
    legacy: 'srs_skole_foraeldre_laerer',
    title: 'SRS skole',
    respondents: [
      { field: 'srs_skole_foraeldre', label: 'Forældre' },
      { field: 'srs_skole_laerer', label: 'Lærer' },
    ],
  }),
  schemeGroup({
    parent: 'vineland_førskole',
    legacy: 'vineland_førskole_foraeldre_laerer',
    title: 'VINELAND førskole',
    respondents: [
      { field: 'vineland_førskole_foraeldre', label: 'Forældre' },
      { field: 'vineland_førskole_laerer', label: 'Lærer' },
    ],
  }),
  schemeGroup({
    parent: 'vineland_skole',
    legacy: 'vineland_skole_foraeldre_laerer',
    title: 'VINELAND skole',
    respondents: [
      { field: 'vineland_skole_foraeldre', label: 'Forældre' },
      { field: 'vineland_skole_laerer', label: 'Lærer' },
    ],
  }),
  { kind: 'andet', field: 'andet', label: 'Andet' },
];

export const ASSESSMENT_RESPONDENT_GROUPS: readonly AssessmentRespondentGroup[] =
  ASSESSMENT_SCHEME_ITEMS.flatMap((item) => (item.kind === 'group' ? [item.group] : []));

export function assessmentSchemeItemKey(item: AssessmentSchemeItem): string {
  switch (item.kind) {
    case 'group':
      return item.group.parent;
    case 'flag':
      return item.field;
    case 'andet':
      return item.field;
    default: {
      const _exhaustive: never = item;
      return _exhaustive;
    }
  }
}

export function assessmentSchemeItemTitle(item: AssessmentSchemeItem): string {
  switch (item.kind) {
    case 'group':
      return item.group.title;
    case 'flag':
      return item.label;
    case 'andet':
      return item.label;
    default: {
      const _exhaustive: never = item;
      return _exhaustive;
    }
  }
}

export function findAssessmentRespondentGroup(
  field: AssessmentFlagKey,
): AssessmentRespondentGroup | undefined {
  return ASSESSMENT_RESPONDENT_GROUPS.find(
    (g) =>
      g.parent === field ||
      g.legacy === field ||
      g.respondents.some((r) => r.field === field),
  );
}

export function isAssessmentParentOn(
  schemes: AssessmentSchemes | undefined,
  group: AssessmentRespondentGroup,
): boolean {
  if (!schemes) return false;
  return Boolean(schemes[group.parent] ?? schemes[group.legacy]);
}

export function isAssessmentRespondentOn(
  schemes: AssessmentSchemes | undefined,
  field: AssessmentFlagKey,
  group: AssessmentRespondentGroup,
): boolean {
  if (!schemes) return false;
  const value = schemes[field];
  if (typeof value === 'boolean') return value;
  return Boolean(schemes[group.legacy]);
}

export function isAssessmentSlotRequested(
  schemes: AssessmentSchemes | undefined,
  fieldName: string,
): boolean {
  if (!schemes) return false;
  const direct = schemes[fieldName as AssessmentFlagKey];
  if (typeof direct === 'boolean' && direct) return true;

  const group = ASSESSMENT_RESPONDENT_GROUPS.find(
    (g) => g.legacy === fieldName || g.respondents.some((r) => r.field === fieldName),
  );
  if (!group) return false;
  if (fieldName === group.legacy) return Boolean(schemes[group.legacy]);
  return isAssessmentRespondentOn(schemes, fieldName as AssessmentFlagKey, group);
}

export function applyAssessmentToggle(
  schemes: AssessmentSchemes,
  field: AssessmentFlagKey,
  newValue: boolean,
): AssessmentSchemes {
  const group = findAssessmentRespondentGroup(field);
  if (!group || field === group.legacy) {
    return { ...schemes, [field]: newValue };
  }

  if (field === group.parent) {
    return {
      ...schemes,
      [group.parent]: newValue,
      ...(newValue ? {} : { [group.legacy]: false }),
    };
  }

  const respondents: AssessmentSchemes = {};
  for (const respondent of group.respondents) {
    if (respondent.field === field) {
      respondents[respondent.field] = newValue;
    } else if (typeof schemes[respondent.field] === 'boolean') {
      respondents[respondent.field] = schemes[respondent.field];
    } else {
      respondents[respondent.field] = Boolean(schemes[group.legacy]);
    }
  }

  return {
    ...schemes,
    ...respondents,
    [group.parent]: true,
    [group.legacy]: false,
  };
}
